import qs from 'qs'
import { bizURL, mgkLoginURL } from '@/config'
import md5 from 'md5'

export const getCookie = (name) => {
  if (document.cookie.length > 0) {
    let start = document.cookie.indexOf(name + '=')
    if (start !== -1) {
      start = start + name.length + 1
      let end = document.cookie.indexOf(';', start)
      if (end === -1) end = document.cookie.length
      return unescape(document.cookie.substring(start, end))
    }
  }
  return ''
}

export const getQuery = (key) => {
  // 获取URL中的查询参数
  const { search, hash } = window.location
  let queries = qs.parse(search.substring(1)) || {}

  // 如果在URL查询参数中找到了key，直接返回
  if (queries[key] !== undefined) {
    return queries[key]
  }

  // 在哈希路由模式下，检查哈希部分是否包含查询参数
  if (hash && hash.includes('?')) {
    // 提取哈希部分中的查询参数 (#/route?param=value)
    const hashQueries = qs.parse(hash.split('?')[1]) || {}
    return hashQueries[key]
  }

  return undefined
}

export const replaceUrl = (replaceQueries = {}) => {
  const { origin, pathname, search } = window.location
  const queries = qs.parse(search.substring(1)) || {}
  for (const key in replaceQueries) {
    queries[key] = replaceQueries[key]
  }
  const newUrl = origin + pathname + '?' + qs.stringify(queries)
  window.history.replaceState({ path: newUrl }, '', newUrl)
}

export const hasQuery = (q) => {
  return Number(getQuery(q) || 0)
}
export const isPreload = () => {
  return hasQuery('is_gaoneng_preload')
}
export const isPreRender = () => {
  return +getQuery('pre_render_global') !== 0
}

export const getEnv = (env) => {
  const envDesc = {
    1: 'test',
    2: 'pre',
    3: 'uat',
    4: '' // prod 线上环境不展示
  }

  return envDesc[env] || ''
}

export const getProbableRes = (probabilities = []) => {
  if (probabilities.length === 0) return 0

  const random = Math.random(0, 1)

  const accumulateSums = probabilities.map((p, idx) => {
    return probabilities.slice(0, idx + 1).reduce((a, b) => a + b, 0)
  })

  for (let i = 0; i < accumulateSums.length; i++) {
    const sum = accumulateSums[i]
    const nextSum = accumulateSums[i + 1] || 1
    if (random <= sum) {
      return i
    }
    if (random > sum && random <= nextSum) {
      return i + 1
    }
  }
  return 0
}

/**
 * 判断是否启用多账户功能
 * @returns {boolean}
 */
export const isMultiAccountEnabledBase = () => {
  const isValidDomain = /^(uat-|pre-)?(cm|gaoneng)\.bilibili\.com$/.test(window.location.hostname)
  const accountId = getQuery('account_id')
  // 仅以 account_id 是否存在作为入口判断，不再依赖实验字段或主体参数值
  return isValidDomain && accountId !== undefined
}

// 逻辑修改只要有account_id就走多账号且外网登录
export const isMultiAccountEnabled = () => {
  return isMultiAccountEnabledBase()
}

/**
 * 存储访问令牌
 * @param {string} token 访问令牌
 */
export const storeAccessToken = (token) => {
  window.accessToken = token
  sessionStorage.setItem('access_token', token)
}

/**
 * 获取访问令牌
 * @returns {string|null}
 */
export const getAccessToken = () => {
  return window.accessToken || sessionStorage.getItem('access_token')
}

/**
 * 移除访问令牌
 */
export const removeAccessToken = () => {
  window.accessToken = null
  sessionStorage.removeItem('access_token')
}

/**
 * 为URL添加多账户相关参数
 * @param {string} url - 原始URL
 * @returns {string} - 处理后的URL
 */
export const appendAccountParams = (url) => {
  if (!isMultiAccountEnabled()) return url

  try {
    // 如果是bizURL (经营号URL)，不添加参数直接返回
    if (url.includes(bizURL)) {
      return url
    }

    const accountId = getQuery('account_id')
    const subjectId = getQuery('subject_id')
    const subjectType = getQuery('subject_type')
    const userId = getQuery('userid')

    // 如果没有需要添加的参数，直接返回原始URL
    const hasAccountId = accountId !== undefined
    const hasSubjectId = subjectId !== undefined
    const hasSubjectType = subjectType !== undefined
    const hasUserId = userId !== undefined
    if (!hasAccountId && !hasSubjectId && !hasSubjectType && !hasUserId) return url

    // 构建参数
    const params = []
    if (hasAccountId) params.push(`account_id=${accountId ?? ''}`)
    if (hasSubjectId) params.push(`subject_id=${subjectId ?? ''}`)
    if (hasSubjectType) params.push(`subject_type=${subjectType ?? ''}`)
    if (hasUserId) params.push(`userid=${userId ?? ''}`)
    const paramStr = params.join('&')

    // 检查URL中是否有哈希(#)
    const hashIndex = url.indexOf('#')

    if (hashIndex === -1) {
      // 没有哈希，直接在URL末尾添加参数
      return url.includes('?') ? `${url}&${paramStr}` : `${url}?${paramStr}`
    } else {
      // 有哈希，需要判断哈希后是否包含路由路径
      const hashPart = url.substring(hashIndex)

      // 检查哈希部分是否包含路由路径（如 #/route）
      if (/^#\//.test(hashPart)) {
        // Vue哈希路由模式 - 在哈希后面的路径末尾添加参数
        if (hashPart.includes('?')) {
          // 哈希部分已有参数，使用&连接
          return `${url}&${paramStr}`
        } else {
          // 哈希部分没有参数，使用?连接
          return `${url}?${paramStr}`
        }
      } else {
        // 普通哈希，在哈希前添加参数
        const urlBeforeHash = url.substring(0, hashIndex)
        return urlBeforeHash.includes('?')
          ? `${urlBeforeHash}&${paramStr}${hashPart}`
          : `${urlBeforeHash}?${paramStr}${hashPart}`
      }
    }
  } catch (e) {
    console.error('URL处理错误:', e)
    return url
  }
}

/**
 * 封装页面跳转方法
 * @param {string} url - 目标URL
 * @param {boolean} replace - 是否使用replace方式跳转
 */
export const navigateTo = (url, replace = false) => {
  const processedUrl = appendAccountParams(url)
  if (replace) {
    window.location.replace(processedUrl)
  } else {
    window.location.href = processedUrl
  }
}

/**
 * 在新窗口中打开链接
 * @param {string} url - 目标URL
 */
export const openInNewWindow = (url) => {
  const processedUrl = appendAccountParams(url)
  window.open(processedUrl, '_blank')
}

/**
 * 获取当前环境的域名前缀
 * @returns {string} 环境前缀 ('pre-', 'uat-', 或 '')
 */
export const getEnvironmentPrefix = () => {
  const { hostname } = window.location
  if (hostname.startsWith('pre-')) {
    return 'pre-'
  } else if (hostname.startsWith('uat-')) {
    return 'uat-'
  }
  return ''
}

/**
 * 签名生成
 * "mgk" + "_" + 当前日期(yyyyMMdd) + uid + "_" + buvid
 */
export const getSign = (pageId) => {
  if (window.alita) {
    const pageInfo = window.alita.getPageInfo()
    const { mid = '', buvid = '' } = pageInfo || {}
    if (!mid || !buvid) {
      return ''
    }
    // 当前日期
    const year = new Date().getFullYear()
    const month =
      new Date().getMonth() < 9 ? `0${new Date().getMonth() + 1}` : new Date().getMonth() + 1
    const day = new Date().getDate() < 10 ? `0${new Date().getDate()}` : new Date().getDate()
    const date = `${year}${month}${day}`
    console.log('mgk_${date}_${mid}_${pageId}_${buvid}', `mgk_${date}_${mid}_${pageId}_${buvid}`)
    const sign = md5(`mgk_${date}_${mid}_${pageId}_${buvid}`)
    return sign
  }
}

// 是否启用表单自动保存实验
export const isAutoSaveFormEnabled = () => {
  return +window._globalExp?.auto_save_form === 1
}

/**
 * 自定义速度的平滑滚动
 * @param {number} targetTop - 目标滚动到的顶部位置（px）
 * @param {number} duration - 滚动总时长（ms），值越大速度越慢
 */
export function smoothScroll(targetTop) {
  const expDuration = Number(window._globalExp?.form_auto_scroll_duration)
  const duration = isNaN(expDuration) ? 300 : expDuration
  if (!requestAnimationFrame || typeof requestAnimationFrame !== 'function') {
    window.scrollTo({
      top: targetTop, // todo:留一些顶部间距，避免被固定元素遮挡,需要考虑一下视频组件
      behavior: 'smooth'
    })
    return
  }
  // 获取当前滚动位置
  const startTop = window.pageYOffset || document.documentElement.scrollTop
  // 计算需要滚动的总距离
  const distance = targetTop - startTop
  // 记录滚动开始的时间戳
  const startTime = performance.now()

  // 逐帧更新滚动位置
  function scroll(currentTime) {
    // 计算已滚动的时间（当前时间 - 开始时间）
    const elapsedTime = currentTime - startTime
    // 计算时间进度（0-1之间，0表示刚开始，1表示结束）
    const progress = Math.min(elapsedTime / duration, 1)

    // 缓动函数：使滚动先慢后快再慢（可选，也可直接用线性滚动）
    // 这里用了easeOutQuad缓动，让结束时更平缓
    const easeProgress = 1 - (1 - progress) * (1 - progress)

    // 计算当前应滚动到的位置
    const currentTop = startTop + distance * easeProgress
    window.scrollTo(0, currentTop)

    // 如果还没到目标时间，继续下一帧
    if (elapsedTime < duration) {
      requestAnimationFrame(scroll)
    }
  }

  // 开始滚动
  requestAnimationFrame(scroll)
}
